import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store';
import { chatsApi } from '@/lib/api/chats';
import { CONTINUATION_FRESH_MS, continuationKey, continuationOwner, readContinuation,
  rememberContinuation, validContinuation } from '@/lib/discoveryContinuationCache';

export default function useDiscoveryContinuations(candidates, mode, enabled) {
  const userId = useAuthStore(s => s.user?.id);
  const session = useAuthStore(s => s._sessionGeneration);
  const key = JSON.stringify([userId, session, mode, enabled, candidates.map(item => [item.conversationId, item.updatedAt])]);
  const owner = enabled && userId ? continuationOwner(userId, session, mode) : null;
  const [, refresh] = useState(0);
  useEffect(() => {
    if (!owner) return;
    let active = true, timer;
    const notify = () => { if (active) refresh(value => value + 1); };
    owner.listeners.add(notify);
    const currentSession = () => {
      const auth = useAuthStore.getState();
      return !owner.disposed && !auth._isSigningOut && auth.user?.id === userId && auth._sessionGeneration === session
        && auth.user?.preferences?.chat?.followUpSuggestionsEnabled !== false;
    };
    const fetchSuggestion = candidate => {
      const entryKey = continuationKey(candidate);
      if (!owner.pending.has(entryKey)) {
        const request = chatsApi.fetchDiscoveryContinuation(candidate.conversationId, mode).then(({ suggestion }) => {
          if (currentSession()) rememberContinuation(owner, candidate, validContinuation(suggestion, candidate) ? suggestion : null);
        }).catch(() => {
          // Retain a usable same-revision prompt during transient failures.
          if (currentSession() && !readContinuation(owner, candidate)?.suggestion) rememberContinuation(owner, candidate, null);
        }).finally(() => owner.pending.delete(entryKey));
        owner.pending.set(entryKey, request);
      }
      return owner.pending.get(entryKey);
    };
    const load = async () => {
      await owner.ready;
      if (!active || !currentSession()) return;
      notify();
      let index = 0;
      const worker = async () => {
        while (active && currentSession() && index < candidates.length) {
          const candidate = candidates[index++];
          if (!readContinuation(owner, candidate)?.fresh) await fetchSuggestion(candidate);
        }
      };
      // Start on landing, with two bounded requests instead of a three-call
      // waterfall. Shared pending entries also deduplicate remounts.
      await Promise.all([worker(), worker()]);
      if (active && currentSession()) timer = setTimeout(load, CONTINUATION_FRESH_MS);
    };
    load();
    return () => { active = false; clearTimeout(timer); owner.listeners.delete(notify); };
  }, [key, owner]); // Every account, mode, eligibility and conversation revision is captured.
  if (!owner) return [];
  return candidates.map(candidate => {
    const entry = readContinuation(owner, candidate), suggestion = entry?.suggestion;
    return { ...candidate, title: suggestion?.prompt, prompt: suggestion?.prompt || null,
      status: suggestion ? 'ready' : entry ? 'unavailable' : 'loading' };
  });
}
